import React, { useEffect, useMemo, useState } from 'react';
import {
  Table, Input, Drawer, Form, Select, Button, Tag, Typography, message, Space,
} from 'antd';
import { EditOutlined, SearchOutlined } from '@ant-design/icons';
import {
  getEmployees, getDepartments, updateEmployee, AdminEmployee, ORG_LEVELS, STATUSES,
} from '../../services/employeeAdminService';

const { Title, Paragraph, Text } = Typography;

const levelColor: Record<string, string> = {
  ceo: 'purple', executive: 'geekblue', director: 'blue', manager: 'cyan', employee: 'default',
};
const statusColor: Record<string, string> = {
  Active: 'green', OnLeave: 'orange', Inactive: 'default', Terminated: 'red',
};

const EmployeeManagementPage: React.FC = () => {
  const [rows, setRows] = useState<AdminEmployee[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [allEmployees, setAllEmployees] = useState<AdminEmployee[]>([]); // for the manager picker
  const [editing, setEditing] = useState<AdminEmployee | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const deptMap = useMemo(() => new Map(departments.map((d) => [d.id, d.name])), [departments]);

  const load = async (p = page, q = query) => {
    setLoading(true);
    try {
      const data = await getEmployees({ page: p, page_size: pageSize, query: q || undefined });
      setRows(data.items); setTotal(data.total); setPage(data.page);
    } catch { message.error('Could not load employees.'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load(1, '');
    getDepartments().then(setDepartments).catch(() => {});
    // load all employees once for the manager dropdown
    getEmployees({ page: 1, page_size: 200 }).then((d) => setAllEmployees(d.items)).catch(() => {});
  }, []);

  const openEdit = (emp: AdminEmployee) => {
    setEditing(emp);
    form.setFieldsValue({
      position: emp.position, org_level: emp.org_level || 'employee', status: emp.status || 'Active',
      department_id: emp.department_id || undefined, manager_id: emp.manager_id || undefined,
      national_id: emp.national_id || '', nssf: emp.nssf || '', tin: emp.tin || '',
    });
  };

  const save = async () => {
    if (!editing) return;
    const vals = await form.validateFields();
    setSaving(true);
    try {
      // Only send changed/non-empty HR fields; blank statutory ids -> null (clears).
      const payload: any = {
        position: vals.position, org_level: vals.org_level, status: vals.status,
        department_id: vals.department_id || null, manager_id: vals.manager_id || null,
        national_id: vals.national_id || null, nssf: vals.nssf || null, tin: vals.tin || null,
      };
      await updateEmployee(editing.id, payload);
      message.success('Employee updated.');
      setEditing(null); load();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      message.error(Array.isArray(d) ? d.map((x: any) => x.msg).join('; ') : (d || 'Could not save.'));
    } finally { setSaving(false); }
  };

  const name = (e: AdminEmployee) => `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.email;

  const columns = [
    { title: 'Name', key: 'name', render: (_: any, e: AdminEmployee) => (
      <div><div>{name(e)}</div><Text type="secondary" style={{ fontSize: 12 }}>{e.email}</Text></div>
    ) },
    { title: 'Position', dataIndex: 'position', key: 'position' },
    { title: 'Department', key: 'dept', render: (_: any, e: AdminEmployee) => (e.department_id && deptMap.get(e.department_id)) || '—' },
    { title: 'Level', dataIndex: 'org_level', key: 'org_level', render: (l: string) => <Tag color={levelColor[l] || 'default'}>{l || 'employee'}</Tag> },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (s: string) => <Tag color={statusColor[s] || 'default'}>{s || 'Active'}</Tag> },
    { title: 'Statutory IDs', key: 'ids', render: (_: any, e: AdminEmployee) => (
      <Space size={4}>
        <Tag color={e.national_id ? 'green' : 'default'}>NIDA {e.national_id ? '✓' : '—'}</Tag>
        <Tag color={e.nssf ? 'green' : 'default'}>NSSF {e.nssf ? '✓' : '—'}</Tag>
        <Tag color={e.tin ? 'green' : 'default'}>TIN {e.tin ? '✓' : '—'}</Tag>
      </Space>
    ) },
    { title: '', key: 'actions', render: (_: any, e: AdminEmployee) => <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(e)}>Edit</Button> },
  ];

  return (
    <div>
      <Title level={3}>Employees</Title>
      <Paragraph type="secondary">
        Manage HR-controlled fields employees can't change themselves — National ID, NSSF, TIN, job title,
        department, organization level, reporting manager and status.
      </Paragraph>

      <Input
        allowClear prefix={<SearchOutlined />} placeholder="Search by name, email or title…"
        style={{ maxWidth: 360, marginBottom: 16 }}
        onChange={(e) => { setQuery(e.target.value); }}
        onPressEnter={() => load(1, query)}
      />

      <Table
        rowKey="id" loading={loading} columns={columns as any} dataSource={rows}
        pagination={{ current: page, pageSize, total, onChange: (p) => load(p, query), showSizeChanger: false }}
      />

      <Drawer
        title={editing ? `Edit ${name(editing)}` : ''} width={460} open={!!editing}
        onClose={() => setEditing(null)}
        extra={<Button type="primary" loading={saving} onClick={save}>Save</Button>}
      >
        {editing && (
          <Form form={form} layout="vertical">
            <Form.Item label="Email"><Input value={editing.email} disabled /></Form.Item>
            <Form.Item label="Job title" name="position"><Input /></Form.Item>
            <Form.Item label="Department" name="department_id">
              <Select allowClear options={departments.map((d) => ({ value: d.id, label: d.name }))} placeholder="Select department" />
            </Form.Item>
            <Form.Item label="Organization level" name="org_level">
              <Select options={ORG_LEVELS.map((l) => ({ value: l, label: l[0].toUpperCase() + l.slice(1) }))} />
            </Form.Item>
            <Form.Item label="Reports to (manager)" name="manager_id">
              <Select
                allowClear showSearch optionFilterProp="label" placeholder="Select manager"
                options={allEmployees.filter((e) => e.id !== editing.id).map((e) => ({ value: e.id, label: `${name(e)} · ${e.position || ''}` }))}
              />
            </Form.Item>
            <Form.Item label="Status" name="status">
              <Select options={STATUSES.map((s) => ({ value: s, label: s }))} />
            </Form.Item>

            <Typography.Title level={5} style={{ marginTop: 8 }}>Statutory identifiers</Typography.Title>
            <Form.Item label="National ID (NIDA)" name="national_id" rules={[{ pattern: /^\d{20}$/, message: '20 digits', validateTrigger: 'onSubmit' }]}>
              <Input placeholder="20 digits" />
            </Form.Item>
            <Form.Item label="NSSF number" name="nssf" rules={[{ pattern: /^\d{8}$/, message: '8 digits' }]}>
              <Input placeholder="8 digits" />
            </Form.Item>
            <Form.Item label="TIN" name="tin" rules={[{ pattern: /^\d{9}$/, message: '9 digits' }]}>
              <Input placeholder="9 digits" />
            </Form.Item>
          </Form>
        )}
      </Drawer>
    </div>
  );
};

export default EmployeeManagementPage;
